import _ from 'lodash';
import eventBus from 'components/form/validator/eventBus';
import validators from 'components/form/validator/validations';

/**
 * Types of event to watch for on any input
 * @type {Array<string>}
 */
const inputEvent = ['blur', 'keyup', 'input', 'focus', 'click'];

/**
 * Standard state template
 * @return {{$touched: boolean, $valid: boolean, $dirty: boolean, $invalid, $clean, $untouched, $error}}
 */
const stateTemplate = ctrl => ({
    // User has interacted and left field
    $touched: false,
    // Is the field valid for all rules
    $valid: false,
    // Has the field been changed
    $dirty: false,
    get $invalid() {
        return !this.$valid;
    },
    get $clean() {
        return !this.$dirty;
    },
    get $untouched() {
        return !this.$touched;
    },
    get $error() {
        return this.$invalid && (this.$touched || ctrl._isDisabled);
    },
});

/**
 * Field control class
 */
export default class FieldControl {
    /**
     * Set up field after being created in directive.
     *
     * @param el
     * @param bindings
     * @param vnode
     */
    constructor(el, bindings, vnode) {
        this.name = el.name;
        this._rules = FieldControl.determineRulesFromEl(el);
        this._type = el.type || 'select';
        this._isDisabled = !!el.disabled;
        this._vm = vnode.context;
        this._el = el;
        this._component = vnode.child;
        this._state = stateTemplate(this);
        this._intitalValue = this._determineValue();
        this._parseBindings(bindings);
    }


    /**
     * Getter for state, immutable
     * @return {{$touched: boolean, $valid: boolean, $dirty: boolean, $invalid, $clean, $untouched, $error}}
     */
    get state() {
        return this._state;
    }

    /**
     * Trigger and return if valid.
     *
     * @param {boolean} value
     */
    validate(value = this._determineValue()) {
        const valid = this._rules.reduce((valid, rule) => !valid ? valid : this._runRule(rule, value), true);
        this._updateState('$valid', valid);
        return valid;
    }

    /**
     * Generate a event handler call back
     *
     * @param {string} type
     * @return {function(*=)}
     */
    eventHandler(type) {
        /**
         * Event handler with optional event/value.
         *
         * @param {Event|string} [event]
         */
        return (event) => {
            const value = this._determineValue(event);
            switch (type) {
            case 'update': // When updated to field
                // Only update dirty and touched states for components
                this._component && this._determineDirty(value) && this._updateState('$touched', true);
                // Validate
                this.validate(value);
                break;
            case 'input': // When updated by "input" vue(?) event
            case 'change': // or change
                this._determineDirty(value);
                break;
            case 'blur': // Then enact blur
                this._updateState('$touched', true);
                break;
            default: // Always
                this.validate(value);
            }
        };
    }

    /**
     * Register events and with any forms on parent view models
     *
     * @return {FieldControl}
     */
    register() {
        if (this._el) {
            this._bindInputEvents();
        }
        if (this._component) {
            this._bindComponentEvents();
        }
        this.eventHandler('created')(this._intitalValue);
        this._registerToForm();

        return this;
    }

    /**
     * Clean up
     * @return {FieldControl}
     */
    unregister() {
        if (this._el) {
            this._unbindInputEvents();
        }
        if (this._component) {
            this._unBindComponentEvents();
        }
        this._unregisterFromForm();

        return this;
    }

    updateBindings({ value, oldValue }) {
        if (value === oldValue) return;

        this._parseBindings(value);
    }

    updateItemChangeOnEl(el) {
        this._isDisabled = !!el.disabled;
    }

    /**
     * Parse bindings from form
     *
     * @param modifiers
     * @param value
     * @param arg
     * @return {this}
     * @private
     */
    _parseBindings({ modifiers = {}, value, arg = this.name, rules }) {
        Object.keys(modifiers).forEach(this._appendRules.bind(this));
        this.name = arg;
        switch (typeof value) {
        case 'string' :
            return this._appendRules(value);
        case 'object':
            if (value.type) {
                this._type = value.type;
            }
            if (value.name) {
                this.name = value.name;
            }
            if (value.rules) {
                return this._appendRules(value.rules);
            }
        }
        if (rules) this._appendRules(rules);
        return this;
    }

    /**
     * Update the field state, it has changed, update the form state
     *
     * @param {string} state the $state to update
     * @param {boolean} value the state to change to
     * @return {boolean} the value passed in, use full for functional style
     * @private
     */
    _updateState(state, value) {
        if (this._state[state] === value) {
            return value;
        }
        this._state[state] = value;
        eventBus.trigger('validator.field.stateChange', state);

        return value;
    }

    /**
     * Append some new rules to set. ensures unique.
     *
     * Designed for use with binds where they can be string of format "min:value" or "required|min:value"
     * Or arrays from objects
     *
     * @param {Array<string>|string|*} rules
     * @return {boolean} if rules have updated
     * @private
     */
    _appendRules(rules) {
        if (!rules) return false;
        if (typeof rules === 'string') {
            rules = rules.split('|');
        }
        if (Array.isArray(rules)) {
            return this._updateRules(rules);
        }
        return false;
    }

    /** *
     * Add rules to rule array
     *
     * @param {[]} rules new rules to add
     * @return {boolean} true if updated
     * @private
     */
    _updateRules(rules) {
        const previous = JSON.stringify(this._rules);
        // Append the rules is they are different
        this._rules = _.uniqBy(this._rules.concat(rules).reverse(), rule => rule.split(':')[0]);

        return JSON.stringify(this._rules) !== previous;
    }


    /**
     * Run a rule in format rules:option1:option2
     *
     * Returns the results of the for this types of field
     *
     * @param {string} rule
     * @param {any} value
     * @return {boolean}
     * @private
     */
    _runRule(rule, value) {
        const ruleParts = rule.split(':');
        return validators[this._type][ruleParts.shift()](value, ...ruleParts);
    }

    /**
     * Is the value now or passed the same a previously and update state
     *
     * @param {any} [value] current value
     * @return {boolean}
     * @private
     */
    _determineDirty(value = this._determineValue()) {
        // Exclude select fields, they don't get dirty — prudes.
        if (this._type === 'select-one') {
            return false;
        }

        return this._updateState('$dirty', this._intitalValue !== value)
    }

    /**
     * Register with parent form.
     *
     * If the there is direct parent form, register to that else broadcast through events to bind
     *
     * @private
     */
    _registerToForm() {
        let vm = this._vm;
        while (vm && !vm.$formCtrl) {
            vm = vm.$parent;
        }
        if (vm && vm.$formCtrl) {
            vm.$formCtrl.addField(this);
        } else {
            eventBus.trigger('validator.field.register', this);
        }
    }

    /**
     * Remove from parent form.
     *
     * @private
     */
    _unregisterFromForm() {
        eventBus.trigger('validator.field.deregister', this);
    }

    /**
     * Bind to all parent forms.
     * @private
     */
    _bindComponentEvents() {
        this._componentCallBack = this.eventHandler('input');
        this._component.$on('input', this._componentCallBack);
    }

    /**
     * Unbind from component
     *
     * @private
     */
    _unBindComponentEvents() {
        this._component.$off('input', this._componentCallBack);
    }

    /**
     * Bind all inputEvent to the element
     *
     * @private
     */
    _bindInputEvents() {
        this._callbacks = inputEvent.map((event) => {
            const listener = this.eventHandler(event);
            this._el.addEventListener(event, listener);
            return { event, listener };
        });
    }

    /**
     * Remove any currently bound events
     *
     * @private
     */
    _unbindInputEvents() {
        this._callbacks.forEach(({ event, listener }) => {
            this._el.removeEventListener(event, listener);
        });
    }

    /**
     * Determine the current value or optional event.
     *
     * @param {Event|string|number} [event]
     * @return {any}
     * @private
     */
    _determineValue(event) {
        switch (typeof event) {
        case 'number':
        case 'string':
            return event;
        case 'object':
            if (event && event.target) {
                return event.target.value;
            }
            break;
        default:
            return this._component ? this._component.value : this._el.value;
        }
    }

    /**
     * Check for attrs that determine rules
     *
     * @param el
     * @return {Array}
     */
    static determineRulesFromEl(el) {
        const rules = [];
        if (!el) {
            return rules;
        }
        if (el.required) {
            rules.push('required');
        }
        if (el.min) {
            rules.push(`min:${el.min}`);
        }
        if (el.max) {
            rules.push(`max:${el.max}`);
        }
        return rules;
    }

}



// WEBPACK FOOTER //
// ./src/components/form/validator/controls/FieldControl.js